// The script can be loaded by more than one section on a page, so define the element only once.
if (!customElements.get('pdp-sticky-atc')) {
  class PdpStickyAtc extends HTMLElement {
    static STRIP_INTERVAL = 3000;

    constructor() {
      super();
      this.button = null;
    }

    connectedCallback() {
      this.mainButton = document.querySelector('.main-product__atc-btn, .product__info-container .product-form__submit');
      this.button = this.querySelector('[data-sticky-atc-btn]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.qtyEl = this.querySelector('[data-sticky-qty]');
      this.minusBtn = this.querySelector('[data-sticky-minus]');
      this.plusBtn = this.querySelector('[data-sticky-plus]');
      if (this.hasAttribute('data-standalone')) {
        this.connectStandalone();
        return;
      }
      if (!this.mainButton || !this.button) return;

      // connectedCallback runs again if the element is moved in the DOM; bind the clicks only once.
      if (!this.clickBound) {
        this.button.addEventListener('click', () => this.mainButton.click());
        this.minusBtn?.addEventListener('click', () => this.stepQuantity(-1));
        this.plusBtn?.addEventListener('click', () => this.stepQuantity(1));
        this.clickBound = true;
      }

      if (this.hasAttribute('data-always-visible')) {
        this.hidden = false;
      } else {
        this.hidden = false;
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-visible', !entry.isIntersecting);
        });
        this.visibilityObserver.observe(this.mainButton);
      }

      // The main button and price are re-rendered on variant change, so re-read them when they mutate.
      this.syncObserver = new MutationObserver(() => this.sync());
      this.syncObserver.observe(this.mainButton.closest('.product__info-container') || this.mainButton, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });
      this.bindQuantity();
      this.sync();
      this.startStripRotation();
    }

    // Homepage popup bar: no PDP form to mirror, so it keeps its own quantity and adds its own variant.
    connectStandalone() {
      if (!this.button) return;
      const { qtyMin, qtyMax, qtyStep } = this.dataset;
      this.qtyMin = parseInt(qtyMin, 10) || 1;
      this.qtyMax = parseInt(qtyMax, 10) || Infinity;
      this.qtyStep = parseInt(qtyStep, 10) || 1;
      this.quantity = this.quantity ?? this.qtyMin;

      if (!this.clickBound) {
        this.button.addEventListener('click', () => this.addStandalone());
        this.minusBtn?.addEventListener('click', () => this.setQuantity(this.quantity - this.qtyStep));
        this.plusBtn?.addEventListener('click', () => this.setQuantity(this.quantity + this.qtyStep));
        this.clickBound = true;
      }

      this.setQuantity(this.quantity);
      this.startStripRotation();
    }

    setQuantity(value) {
      this.quantity = Math.min(Math.max(value, this.qtyMin), this.qtyMax);
      if (this.qtyEl) this.qtyEl.textContent = this.quantity;
      if (this.minusBtn) this.minusBtn.disabled = this.quantity <= this.qtyMin;
      if (this.plusBtn) this.plusBtn.disabled = this.quantity >= this.qtyMax;
    }

    async addStandalone() {
      if (this.button.disabled) return;
      this.button.disabled = true;

      try {
        const response = await fetch(window.routes?.cart_add_url ?? '/cart/add.js', {
          ...fetchConfig('javascript'),
          body: JSON.stringify({
            items: [{ id: Number(this.dataset.variantId), quantity: this.quantity }],
            sections: ['cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          this.showStandaloneError(response.status === 422 && data.description ? data.description : window.cartStrings?.error);
          return;
        }

        const bubble = document.getElementById('cart-icon-bubble');
        const bubbleHtml = data.sections?.['cart-icon-bubble'];
        if (bubble && bubbleHtml) {
          bubble.innerHTML =
            new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section')?.innerHTML ?? '';
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-sticky-atc', productVariantId: this.dataset.variantId, cartData: data });
        this.setQuantity(this.qtyMin);
        this.dispatchEvent(new CustomEvent('sticky-atc:added', { bubbles: true }));

        // Live store: Shiprocket Smart Cart reloads its items when opened with true.
        if (typeof window.slideShiprocketSmartCartInFrame === 'function') {
          window.slideShiprocketSmartCartInFrame(true);
        } else {
          window.location.href = window.routes?.cart_url ?? '/cart';
        }
      } catch (error) {
        console.error(error);
        this.showStandaloneError(window.cartStrings?.error);
      } finally {
        this.button.disabled = false;
      }
    }

    // The bar has no room for a message line, so the button shows the error for a moment.
    showStandaloneError(message) {
      if (!message) return;
      clearTimeout(this.errorTimer);
      this.button.textContent = message;
      this.errorTimer = setTimeout(() => {
        this.button.textContent = this.dataset.labelAdd;
      }, 3000);
    }

    disconnectedCallback() {
      if (this.onMainQtyChange) {
        document.removeEventListener('change', this.onMainQtyChange);
        document.removeEventListener('input', this.onMainQtyChange);
        this.qtyBound = false;
      }
      this.visibilityObserver?.disconnect();
      this.syncObserver?.disconnect();
      clearInterval(this.stripTimer);
    }

    // With more than one strip message, show each for STRIP_INTERVAL, then fade to the next one.
    startStripRotation() {
      clearInterval(this.stripTimer);
      this.stripItems = [...this.querySelectorAll('[data-strip-item]')];
      if (this.stripItems.length < 2) return;

      this.stripIndex = Math.max(0, this.stripItems.findIndex((item) => item.classList.contains('is-active')));
      this.stripTimer = setInterval(() => this.showNextStripItem(), PdpStickyAtc.STRIP_INTERVAL);
    }

    showNextStripItem() {
      const current = this.stripItems[this.stripIndex];
      this.stripIndex = (this.stripIndex + 1) % this.stripItems.length;
      const next = this.stripItems[this.stripIndex];

      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
    }

    // Quantity input of the PDP form; looked up each time because variant changes can re-render it.
    get mainQtyInput() {
      return this.mainButton.closest('form')?.querySelector('input[name="quantity"]') ?? null;
    }

    // The bar and the PDP quantity selector always show the same number: either one can change it.
    bindQuantity() {
      if (this.qtyBound) return;
      this.qtyBound = true;

      this.onMainQtyChange = (event) => {
        if (event.target === this.mainQtyInput) this.renderQuantity();
      };
      document.addEventListener('change', this.onMainQtyChange);
      document.addEventListener('input', this.onMainQtyChange);
    }

    // Moves the PDP input by one step (respecting its min / max / step), as its own +/- buttons do.
    stepQuantity(direction) {
      const main = this.mainQtyInput;
      if (!main) return;
      const before = main.value;
      if (direction > 0) main.stepUp();
      else main.stepDown();
      if (main.value !== before) main.dispatchEvent(new Event('change', { bubbles: true }));
      this.renderQuantity();
    }

    renderQuantity() {
      const main = this.mainQtyInput;
      if (!main || !this.qtyEl) return;
      const value = parseInt(main.value, 10) || 1;
      this.qtyEl.textContent = value;
      if (this.minusBtn) this.minusBtn.disabled = value <= (parseInt(main.min, 10) || 1);
      if (this.plusBtn) this.plusBtn.disabled = main.max !== '' && value >= parseInt(main.max, 10);
    }

    sync() {
      // Descaler PDPs show the price inside the button; the standard PDP shows it in the price block.
      const mainPrice =
        this.mainButton.querySelector('.main-product__atc-btn-price') ||
        document.querySelector('.product__info-container .price-item--last');
      if (mainPrice && this.priceEl) this.priceEl.textContent = mainPrice.textContent.trim();

      // Decide "sold out" from the main button's label, not its disabled state: other scripts on the page
      // (e.g. checkout apps) disable the button while an add-to-cart is in progress.
      const mainLabel = this.mainButton.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      const soldOutLabels = [this.dataset.labelSoldOut, this.dataset.labelUnavailable]
        .filter(Boolean)
        .map((label) => label.trim().toLowerCase());
      const soldOut = soldOutLabels.includes(mainLabel);

      // While an add-to-cart is in progress the main button is disabled (or loading); mirror that
      // faded, unclickable look on the sticky button, keeping its normal label.
      const isProcessing =
        this.mainButton.disabled ||
        this.mainButton.getAttribute('aria-disabled') === 'true' ||
        this.mainButton.classList.contains('loading');
      this.button.disabled = soldOut || isProcessing;
      this.button.textContent = soldOut ? this.dataset.labelSoldOut : this.dataset.labelAdd;
      this.renderQuantity();
    }
  }

  customElements.define('pdp-sticky-atc', PdpStickyAtc);
}
